import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import { listaComBuscaOuTriagem } from './inbox-debug';
import type { TicketsListResponse } from './types';

/**
 * Saiu da aba Fechados (pra outra aba ou outra tela): a lista volta pra 1ª
 * página.
 *
 * Toda rebusca de uma lista paginada rebusca TODAS as páginas carregadas
 * (comportamento do TanStack): quem rolou Fechados fundo pagava N buscas a cada
 * volta da conexão. Mesma ideia do histórico da conversa, que já é aparado ao
 * sair (`BLOCOS_GUARDADOS_AO_SAIR`). Voltando à aba, rolar busca a página 2 de
 * novo pelo cursor da 1ª.
 *
 * Só a lista normal: busca e triagem são outras listas, sempre vindas do
 * servidor, e ficam como estão. Lista no meio de uma busca de página também
 * fica — aparar ali brigaria com a página que está chegando.
 *
 * Escreve no cache sem "lavar" a cópia: `setQueryData` apaga a marca de velha
 * e renova o `dataUpdatedAt` (vide `reconexao.ts`). O carimbo é preservado e,
 * se a lista estava marcada, a marca é reaplicada.
 */
export function voltarFechadosPraPrimeiraPagina(qc: QueryClient): number {
  let aparadas = 0;
  for (const q of qc.getQueryCache().findAll({ queryKey: ['tickets', 'closed'] })) {
    if (listaComBuscaOuTriagem(q.queryKey)) continue;
    if (q.state.fetchStatus === 'fetching') continue;
    const dados = q.state.data as InfiniteData<TicketsListResponse, unknown> | undefined;
    if (!dados || dados.pages.length <= 1) continue;
    const estavaVelha = q.state.isInvalidated;
    qc.setQueryData<InfiniteData<TicketsListResponse, unknown>>(
      q.queryKey,
      { pages: dados.pages.slice(0, 1), pageParams: dados.pageParams.slice(0, 1) },
      { updatedAt: q.state.dataUpdatedAt },
    );
    if (estavaVelha) {
      void qc.invalidateQueries({ queryKey: q.queryKey, exact: true, refetchType: 'none' });
    }
    aparadas++;
  }
  return aparadas;
}
